import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/solid';
import type {ChangeEvent, JSX} from 'react';
import {useEffect, useState} from 'react';

import type {queryParamsType} from '@/hooks/useFilters';

import Button from './Button';
import PaginationInput from './PaginationInput';

export type PaginationMode = 'page' | 'offset';

export type PaginationProps = {
  // Common props
  limit?: number;
  isSearch?: boolean;

  // Mode selection
  mode?: PaginationMode;

  // Page-based pagination props
  total?: number;
  isPageReset?: boolean;
  currentPageOverride?: number; // 1-indexed; lets parent sync the displayed page after direct fetches
  onPageChange?: (queryParams: queryParamsType) => Promise<void>;

  // Offset-based pagination props
  offset?: number;
  hasMore?: boolean;
  isEmpty?: boolean;
  onOffsetChange?: (offset: number, limit: number) => Promise<void>;
};

/**
 * `onPageChange` / `onOffsetChange` are declared promise-returning, but most
 * call sites hand Pagination a plain void-returning callback through an
 * `as PaginationProps['onPageChange']` cast, and the Mansa screens pass a bare
 * `setPage` state setter. So the value really can be `undefined` at runtime:
 * normalise it before attaching a handler instead of calling `.catch` on it.
 * The handler is a no-op because every one of those callbacks already catches
 * and surfaces its own failures; this only stops an unexpected throw becoming
 * an unhandled rejection.
 */
const settle = (result: void | Promise<void>): void => {
  Promise.resolve(result).catch(() => {});
};

export default function Pagination({
  // Common props
  limit = 10,
  isSearch = false,

  // Mode selection
  mode = 'page',

  // Page-based pagination props
  total = 0,
  isPageReset: resetIndicator,
  currentPageOverride,
  onPageChange,

  // Offset-based pagination props
  offset = 0,
  hasMore = false,
  isEmpty = false,
  onOffsetChange,
}: PaginationProps): JSX.Element {
  // For page-based pagination
  const totalPages = Math.ceil(total / limit);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageNumberInput, setPageNumberInput] = useState<number>(1);

  // For offset-based pagination
  const currentOffsetPage = Math.floor(offset / limit) + 1;
  const [offsetPageInput, setOffsetPageInput] =
    useState<number>(currentOffsetPage);
  let displayTotalPages = '';
  let isPreviousDisabled: boolean;
  let isNextDisabled: boolean;
  let isInputDisabled: boolean;

  useEffect(() => {
    // Update input field when offset changes externally
    if (mode === 'offset') {
      setOffsetPageInput(currentOffsetPage);
    }
  }, [offset, limit, currentOffsetPage, mode]);

  // Handle page-based pagination
  const setPage = (page: number) => {
    if (mode === 'page' && onPageChange) {
      setCurrentPage(page);
      setPageNumberInput(page);
      settle(onPageChange({limit, page: page - 1}));
    }
  };

  // Handle offset-based pagination
  const setOffsetPage = (page: number) => {
    if (mode === 'offset' && onOffsetChange) {
      const newOffset = (page - 1) * limit;
      setOffsetPageInput(page);
      settle(onOffsetChange(newOffset, limit));
    }
  };

  useEffect(() => {
    // Reset page when requested (for page-based pagination)
    if (mode === 'page') {
      setPage(1);
    }
  }, [resetIndicator, mode]);

  useEffect(() => {
    // Sync displayed page when parent overrides it (e.g. post-action re-fetch)
    if (mode === 'page' && currentPageOverride !== undefined) {
      setCurrentPage(currentPageOverride);
      setPageNumberInput(currentPageOverride);
    }
  }, [currentPageOverride, mode]);

  // Navigation handlers
  function onPreviousPage() {
    if (mode === 'page' && currentPage > 1) {
      setPage(currentPage - 1);
    } else if (mode === 'offset' && offset >= limit) {
      setOffsetPage(currentOffsetPage - 1);
    }
  }

  function onNextPage() {
    if (mode === 'page' && currentPage < totalPages) {
      setPage(currentPage + 1);
    } else if (mode === 'offset' && hasMore) {
      setOffsetPage(currentOffsetPage + 1);
    }
  }

  function handleGoToPage() {
    if (mode === 'page') {
      if (
        pageNumberInput >= 1 &&
        pageNumberInput <= totalPages &&
        pageNumberInput !== currentPage
      ) {
        setPage(pageNumberInput);
      }
    } else if (mode === 'offset') {
      if (offsetPageInput >= 1 && offsetPageInput !== currentOffsetPage) {
        // Calculate the actual offset for the requested page
        const newOffset = (offsetPageInput - 1) * limit;
        // Update the input display value
        setOffsetPageInput(offsetPageInput);

        // Trigger the offset change with the calculated offset value
        if (onOffsetChange) {
          settle(onOffsetChange(newOffset, limit));
        }
      }
    }
  }

  function handlePageInputChange(event: ChangeEvent<HTMLInputElement>) {
    const value = Number(event.target.value);
    if (mode === 'page') {
      setPageNumberInput(value);
    } else {
      setOffsetPageInput(value);
    }
  }

  // Determine display values based on mode
  const displayCurrentPage = mode === 'page' ? currentPage : currentOffsetPage;
  const displayInputValue = mode === 'page' ? pageNumberInput : offsetPageInput;

  if (mode === 'page') {
    displayTotalPages = totalPages.toString();
  }

  if (mode === 'page') {
    isPreviousDisabled = currentPage === 1;
  } else {
    isPreviousDisabled = offset === 0;
  }

  if (mode === 'page') {
    // `>=`: a page past the end (the last row of the last page was removed)
    // has nowhere further to go either.
    isNextDisabled = currentPage >= totalPages || total === 0;
  } else {
    isNextDisabled = !hasMore || isEmpty;
  }

  if (mode === 'page') {
    isInputDisabled =
      pageNumberInput < 1 ||
      pageNumberInput === currentPage ||
      pageNumberInput > totalPages;
  } else {
    isInputDisabled =
      offsetPageInput < 1 || offsetPageInput === currentOffsetPage;
  }

  const textClass = 'text-sm font-medium text-gray-800 dark:text-white';
  return (
    <footer
      data-cy="admin-pagination"
      className="border border-t-0 py-3 shadow-sm rounded-xl rounded-tr-none rounded-tl-none bg-white dark:bg-transparent dark:border-dark-borderMid px-4 sm:px-8"
    >
      <div className="w-full flex flex-col sm:flex-row sm:justify-between gap-4 sm:gap-0">
        {/* Left side: Page info and input */}
        <div className="flex flex-wrap items-center space-x-2 sm:space-x-4">
          <div className={textClass}>{isSearch ? 'Result' : 'Page'}</div>

          <PaginationInput
            dataCy="admin-pagination-page-input"
            submitDataCy="admin-pagination-go"
            value={displayInputValue}
            onChange={handlePageInputChange}
            onSubmit={handleGoToPage}
            isDisabled={isInputDisabled}
          />

          <div className={textClass}>
            {mode === 'page'
              ? `of ${displayTotalPages}`
              : ` of ${hasMore ? ' many' : displayCurrentPage}`}
          </div>
        </div>

        {/* Right side: Prev / Next buttons */}
        <div className="flex flex-wrap gap-2 sm:gap-3 justify-end">
          <Button
            variant="outline"
            colorScheme="cyan"
            onClick={onPreviousPage}
            disabled={isPreviousDisabled}
            data-cy="admin-pagination-previous"
            leftIcon={<ChevronLeftIcon className="w-5 h-5" />}
          >
            Previous
          </Button>

          <Button
            variant="outline"
            colorScheme="cyan"
            onClick={onNextPage}
            disabled={isNextDisabled}
            data-cy="admin-pagination-next"
            leftIcon={<ChevronRightIcon className="w-5 h-5" />}
          >
            Next
          </Button>
        </div>
      </div>
    </footer>
  );
}
